import React, { useEffect, useState } from 'react';

const ScrollIndicator = ({ targetElement, isActive = false }) => {
  const [arrowPosition, setArrowPosition] = useState(null);
  const [direction, setDirection] = useState(null);

  useEffect(() => {
    if (!isActive || !targetElement) {
      setArrowPosition(null);
      setDirection(null);
      return;
    }

    const updateArrowPosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Check if element is out of viewport
      const isOutOfView = 
        rect.bottom < 0 || 
        rect.top > viewportHeight || 
        rect.right < 0 || 
        rect.left > viewportWidth;

      if (!isOutOfView) {
        setArrowPosition(null);
        setDirection(null);
        return;
      }

      // Determine direction and position
      let newDirection = null;
      let newPosition = {};

      if (rect.bottom < 0) {
        // Element is above viewport - show arrow pointing up
        newDirection = 'up';
        newPosition = {
          top: '20px',
          left: `${Math.min(Math.max(rect.left + rect.width / 2, 50), viewportWidth - 50)}px`,
          transform: 'translateX(-50%)'
        };
      } else if (rect.top > viewportHeight) {
        // Element is below viewport - show arrow pointing down
        newDirection = 'down';
        newPosition = {
          bottom: '20px',
          left: `${Math.min(Math.max(rect.left + rect.width / 2, 50), viewportWidth - 50)}px`,
          transform: 'translateX(-50%)'
        };
      } else if (rect.right < 0) {
        // Element is to the left - show arrow pointing left
        newDirection = 'left';
        newPosition = {
          top: `${Math.min(Math.max(rect.top + rect.height / 2, 50), viewportHeight - 50)}px`,
          left: '20px',
          transform: 'translateY(-50%)'
        };
      } else if (rect.left > viewportWidth) {
        // Element is to the right - show arrow pointing right
        newDirection = 'right';
        newPosition = {
          top: `${Math.min(Math.max(rect.top + rect.height / 2, 50), viewportHeight - 50)}px`,
          right: '20px',
          transform: 'translateY(-50%)'
        };
      }

      setDirection(newDirection);
      setArrowPosition(newPosition);
    };

    // Initial update
    updateArrowPosition();

    // Update on scroll and resize
    const handleUpdate = () => {
      updateArrowPosition();
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Use ResizeObserver for more precise updates
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(handleUpdate);
      resizeObserver.observe(targetElement);
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [targetElement, isActive]);

  if (!isActive || !arrowPosition || !direction) return null;

  const getArrowSymbol = () => {
    switch (direction) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      case 'left':
        return '←';
      case 'right':
        return '→';
      default:
        return '→';
    }
  };

  return (
    <div
      className="fixed z-[10000] pointer-events-none"
      style={arrowPosition}
    >
      <div
        className="bg-neutral-900 text-neutral-0 px-4 py-2 font-mono text-2xl font-bold animate-bounce"
        style={{
          animation: 'bounce 1s infinite',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
        }}
      >
        {getArrowSymbol()}
      </div>
    </div>
  );
};

export default ScrollIndicator;


